import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { DiscountUpdateEvent } from '@theme/events';

const STORAGE_KEYS = {
  subscribed: 'email-popup:subscribed',
  dismissedUntil: 'email-popup:dismissed-until',
  pending: 'email-popup:pending',
};
const DAY_MS = 24 * 60 * 60 * 1000;
const MOBILE_QUERY = '(max-width: 749px)';
// Retry later instead of stacking on top of another open modal (cart drawer, search)
const BUSY_RETRY_MS = 10000;

/**
 * Storage can throw (private mode, blocked site data); the popup still works without it.
 * @param {Storage} storage
 * @param {string} key
 * @returns {string | null}
 */
function readStorage(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @param {string | null} value - `null` removes the key
 */
function writeStorage(storage, key, value) {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch {
    // Ignore: the popup may show again next visit, which is acceptable
  }
}

/**
 * Email capture popup. Opens after a delay or on exit intent, subscribes through
 * Shopify's customer form, then reveals and optionally applies the discount code.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} email
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} error
 * @property {HTMLElement} formView
 * @property {HTMLElement} successView
 * @property {HTMLElement} successHeading
 * @property {HTMLElement} status
 * @property {HTMLElement} [codeValue]
 *
 * @extends {DialogComponent}
 */
class EmailPopupComponent extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'email', 'submitButton', 'error', 'formView', 'successView', 'successHeading', 'status'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timer;

  #subscribed = false;

  /** Refs typed for this component; the base class types only `dialog`. */
  get #elements() {
    return /** @type {Refs} */ (this.refs);
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.#elements.form.addEventListener('submit', this.#handleSubmit, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose, { signal });

    // In the theme editor, show the popup while its section is selected
    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener('shopify:section:select', this.#handleEditorSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect, { signal });
      return;
    }

    // Returning from a normal (non-AJAX) submit: this page rendered the form's result marker
    if (readStorage(sessionStorage, STORAGE_KEYS.pending)) {
      writeStorage(sessionStorage, STORAGE_KEYS.pending, null);
      const { outcome, message } = this.#readResult(document, this.#elements.form.id);

      if (outcome === 'success' || window.location.search.includes('customer_posted=true')) {
        this.#showSuccess();
        this.showDialog();
        this.#applyDiscount();
        return;
      }

      if (outcome === 'error') {
        this.#showError(message || this.dataset.errorMessage || '');
        this.showDialog();
        return;
      }
    }

    if (!this.#shouldShow()) return;

    this.#schedule(Number(this.dataset.delay ?? 0) * 1000);

    if (this.dataset.exitIntent === 'true' && !window.matchMedia(MOBILE_QUERY).matches) {
      document.documentElement.addEventListener('mouseleave', this.#handleExitIntent, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    window.clearTimeout(this.#timer);
  }

  /** @returns {boolean} */
  #shouldShow() {
    if (readStorage(localStorage, STORAGE_KEYS.subscribed)) return false;

    const dismissedUntil = Number(readStorage(localStorage, STORAGE_KEYS.dismissedUntil));
    if (dismissedUntil && Date.now() < dismissedUntil) return false;

    if (this.dataset.showOnMobile !== 'true' && window.matchMedia(MOBILE_QUERY).matches) return false;

    return true;
  }

  /** @param {number} delay */
  #schedule(delay) {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(this.#open, delay);
  }

  #open = () => {
    if (this.#elements.dialog.open || !this.#shouldShow()) return;

    if (document.querySelector('dialog[open]')) {
      this.#schedule(BUSY_RETRY_MS);
      return;
    }

    document.documentElement.removeEventListener('mouseleave', this.#handleExitIntent);
    window.clearTimeout(this.#timer);
    this.showDialog();
  };

  /** @param {MouseEvent} event */
  #handleExitIntent = (event) => {
    // Leaving through the top edge, towards the tab bar or address bar
    if (event.clientY <= 0) this.#open();
  };

  #handleClose = () => {
    if (this.#subscribed || this.hasAttribute('data-design-mode')) return;

    const days = Number(this.dataset.frequencyDays) || 7;
    writeStorage(localStorage, STORAGE_KEYS.dismissedUntil, String(Date.now() + days * DAY_MS));
  };

  /** @param {Event} event */
  #handleEditorSelect = (event) => {
    if (event instanceof CustomEvent && this.closest(`#shopify-section-${event.detail?.sectionId}`)) {
      this.showDialog();
    }
  };

  /** @param {Event} event */
  #handleEditorDeselect = (event) => {
    if (event instanceof CustomEvent && this.closest(`#shopify-section-${event.detail?.sectionId}`)) {
      this.closeDialog();
    }
  };

  /**
   * Lets the browser submit the form natively. Shopify's spam protection attaches an
   * invisible CAPTCHA token to customer forms during a real submit, so AJAX or scripted
   * submits are rejected with "Missing CAPTCHA token". The page reloads and
   * `connectedCallback` reopens the popup with the result.
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    const { email, submitButton } = this.#elements;

    if (submitButton.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      return;
    }

    if (!email.checkValidity()) {
      event.preventDefault();
      this.#showError(email.validationMessage);
      email.focus();
      return;
    }

    this.#showError('');
    submitButton.setAttribute('aria-busy', 'true');
    writeStorage(sessionStorage, STORAGE_KEYS.pending, 'true');
  };

  /**
   * Reads the signup result marker that email-popup.liquid renders inside this form.
   * @param {Document | Element} root
   * @param {string} formId
   * @returns {{ outcome: 'success' | 'error' | 'unknown', message: string }}
   */
  #readResult(root, formId) {
    const form = root instanceof Document ? root.getElementById(formId) : root.querySelector(`#${CSS.escape(formId)}`);
    const result = form?.querySelector('template[data-email-popup-result]');
    const type = result?.getAttribute('data-email-popup-result');

    if (type === 'success') return { outcome: 'success', message: '' };
    if (type === 'error' && result instanceof HTMLTemplateElement) {
      return { outcome: 'error', message: result.content.textContent?.replace(/\s+/g, ' ').trim() ?? '' };
    }
    return { outcome: 'unknown', message: '' };
  }

  /** @param {string} message */
  #showError(message) {
    const { email, error } = this.#elements;
    error.textContent = message;
    email.setAttribute('aria-invalid', message ? 'true' : 'false');
  }

  #showSuccess() {
    const { formView, successView, successHeading, dialog } = this.#elements;

    this.#subscribed = true;
    writeStorage(localStorage, STORAGE_KEYS.subscribed, 'true');

    formView.hidden = true;
    successView.hidden = false;
    dialog.setAttribute('aria-labelledby', successHeading.id);
    successHeading.focus();
  }

  async #applyDiscount() {
    const code = this.dataset.discountCode;
    if (!code || this.dataset.autoApply !== 'true') return;

    try {
      // Shopify's discount link saves the code for this session, even before a cart exists
      // routes.root_url is "/" or a locale prefix such as "/fr" without a trailing slash
      const root = (this.dataset.rootUrl ?? '/').replace(/\/?$/, '/');
      const response = await fetch(
        `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`,
        { signal: this.#abortController.signal }
      );
      if (!response.ok) return;

      const cart = await response.json();
      document.dispatchEvent(new DiscountUpdateEvent(cart, this.id));
      this.#elements.status.textContent = this.dataset.appliedMessage ?? '';
    } catch (error) {
      // The code is still shown, so the customer can enter it at checkout
      if (!this.#abortController.signal.aborted) console.error(error);
    }
  }

  /**
   * Copies the discount code to the clipboard.
   */
  async copyCode() {
    const code = this.dataset.discountCode;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      this.#elements.status.textContent = this.dataset.copiedMessage ?? '';
    } catch {
      // Clipboard can be blocked; select the code so it can be copied manually
      const { codeValue } = this.#elements;
      if (!codeValue) return;
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(codeValue);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }
}

if (!customElements.get('email-popup-component')) {
  customElements.define('email-popup-component', EmailPopupComponent);
}
